// 직원 관리 관련 타입 정의
import { Timestamp } from 'firebase/firestore';
import { TimeRange } from './workTimeTemplate';
import type { LaborDayAllowanceBreakdown } from '../utils/laborDayAllowance';

/** Flutter `EmployeePayType` storageValue 와 1:1 */
export type EmployeePayType =
  | 'monthly'
  | 'hourly'
  | 'dailyWorker'
  | 'businessService';

export function resolvePayType(
  stored: string | null | undefined,
  isHourlyFallback: boolean,
): EmployeePayType {
  switch (stored) {
    case 'dailyWorker':
    case 'businessService':
    case 'monthly':
    case 'hourly':
      return stored;
    default:
      return isHourlyFallback ? 'hourly' : 'monthly';
  }
}

export const isRegularEmployee = (e: Pick<Employee, 'payType' | 'isHourly'>): boolean => {
  const pt = resolvePayType(e.payType, e.isHourly);
  return pt === 'monthly' || pt === 'hourly';
};

export const isDailyWorkerPay = (e: Pick<Employee, 'payType' | 'isHourly'>): boolean =>
  resolvePayType(e.payType, e.isHourly) === 'dailyWorker';

export const isBusinessServicePay = (e: Pick<Employee, 'payType' | 'isHourly'>): boolean =>
  resolvePayType(e.payType, e.isHourly) === 'businessService';

export interface Employee {
  id: string;
  ownerId: string;              // 소유자 ID (Firebase Auth UID)
  name: string;
  mp: number;                    // Manpower 값
  isHourly: boolean;            // 시급제 여부 (legacy — payType 없을 때 fallback)
  payType?: EmployeePayType;    // V52 — Flutter 와 동일 storageValue
  hourlyRate: number;           // 시급
  monthlySalary: number;        // 월급
  dailyRate?: number;           // V52 — 일당·용역 건별 단가
  weeklyOffDays: number;        // 주 휴무일 수
  weeklyWorkHours: number;      // 주 근무 시간
  taxRate: number;              // 원천세 비율
  dependents: number;           // 부양가족 수
  hasFourInsurance: boolean;    // 4대보험 가입 여부
  payday: number;               // 월급 지급일
  age?: number | null;          // 나이 (nullable)
  isweeklyWork: boolean;        // 주휴수당 포함 여부
  account_number: number;       // 가계부 번호 (Flutter와 일치: snake_case)
  accountNumber?: number;       // 호환성을 위한 camelCase (deprecated)
  hire_date?: Timestamp | null; // 입사일 (Flutter와 일치: snake_case, nullable)
  hireDate?: Timestamp | null;  // 호환성을 위한 camelCase (deprecated)
  severance_pay?: number | null; // 퇴직금 (Flutter와 일치: snake_case, nullable)
  severancePay?: number | null; // 호환성을 위한 camelCase (deprecated)
  pensionRate: number;          // 국민연금 비율 (4.5%)
  healthInsuranceRate: number;  // 건강보험 비율 (3.5%)
  employmentInsuranceRate: number; // 고용보험 비율 (0.8%)
  industrialAccidentRate: number;  // 산재보험 비율 (0.65%)
  createdAt: Timestamp;         // 생성일
  updatedAt: Timestamp;         // 수정일
  isRetired?: boolean;          // 퇴직 여부
}

export interface WorkSchedule {
  id: string;
  ownerId: string;              // 소유자 ID (Firebase Auth UID)
  date: string;                 // ISO 8601 형식 (YYYY-MM-DD)
  employee_id: string;          // 직원 ID (detach 시 빈 문자열)
  employee_name: string;        // 직원 이름 (Flutter에서 중복 저장)
  /** V53 — 퇴직자 삭제(근무 기록 유지) 후 orphan 스케줄 표시용 */
  snapshot_employee_name?: string | null;
  snapshotEmployeeName?: string | null;
  // 직원 정보 중복 (Flutter WorkScheduleConverter.toFirebaseMap 구조)
  mp: number;                   // Manpower 값
  isHourly: boolean;            // 시급제 여부
  hourlyRate: number;           // 시급
  monthlySalary: number;        // 월급
  weeklyOffDays: number;        // 주 휴무일 수
  weeklyWorkHours: number;      // 주 근무 시간
  taxRate: number;              // 원천세 비율
  dependents: number;           // 부양가족 수
  hasFourInsurance: boolean;    // 4대보험 가입 여부
  payday: number;               // 월급 지급일
  isweeklyWork: boolean;        // 주휴수당 포함 여부
  hire_date?: any;              // 입사일 (직원 정보 중복)
  severance_pay?: number | null; // 퇴직금 (직원 정보 중복)
  start_time: string;           // 시작 시간 (ISO 8601)
  end_time: string;             // 종료 시간 (ISO 8601)
  is_favorite: boolean;         // 즐겨찾기 여부
  chat_messages: Array<{        // 채팅 메시지
    id: string;
    message: string;
    timestamp: number;
    sender: string;
  }>;
  break_time: string | null;    // 휴게시간 (nullable)
  account_number: number;       // 가계부 번호
  
  // 템플릿 기반 스케줄 (새 구조)
  workTime1?: TimeRange;        // 1차 근무 시간
  workTime2?: TimeRange;        // 2차 근무 시간
  templateId?: string;         // 사용된 템플릿 ID
  templateHourlyRate?: number;  // 시급 (템플릿 사용 시, hourlyRate와 구분)
  
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface EmployeeHistory {
  id: string;
  employeeId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  changes: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
  timestamp: number;
  accountNumber: number;
}

export interface WorkScheduleHistory {
  id: string;
  scheduleId: string;
  employeeId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  changes: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
  timestamp: number;
  accountNumber: number;
}

// 급여 계산 관련 타입
/** Flutter `employee_monthly_info.dart` 1:1 */
export interface EmployeeMonthlyInfo {
  employee: Employee;
  totalWorkHours: number;
  baseSalary: number;
  holidayAllowance: number;
  totalSalary: number;
  hasInsurance: boolean;
  totalEmployeeInsurance: number;
  withholdingTax: number;
  localTax: number;
  laborDayAllowance?: number;
  laborDayIncluded?: boolean;
  /** 일당·용역 근무일(건)수 */
  workDays?: number;
}

export interface PayrollDetail {
  grossSalary: number;          // 총급여
  insurance: {
    pension: number;            // 국민연금
    health: number;             // 건강보험
    longTermCare: number;       // 장기요양보험 (2025년 기준 추가)
    employment: number;         // 고용보험
    industrial: number;         // 산재보험
    total: number;              // 보험료 총합
    breakdown: {
      pension: number;
      health: number;
      longTermCare: number;
      employment: number;
      industrial: number;
    };
  };
  tax: {
    withholding: number;        // 원천세
    local: number;              // 지방세
    total: number;              // 세금 총합
  };
  netSalary: number;            // 실수령액
  dependents: number;           // 부양가족 수
}

export interface MonthlyPayrollData {
  employee: Employee;
  totalWorkHours: number;       // 총 근무시간
  baseSalary: number;           // 기본급
  holidayAllowance: number;     // 주휴수당
  totalSalary: number;          // 총급여
  insurance: PayrollDetail['insurance'];
  tax: PayrollDetail['tax'];
  netSalary: number;            // 실수령액
  weeklyDetails: WeeklyHolidayDetail[];
  dependents: number;
  /** 5인 이상 시급제일 때만: 일반 근로 급여 */
  regularPay?: number;
  /** 5인 이상 시급제일 때만: 연장 수당 */
  overtimePay?: number;
  /** 5인 이상 시급제일 때만: 야간 수당 */
  nightPay?: number;
  /** 5인 이상 시급제일 때만: 휴일 수당 */
  holidayPay?: number;
  /**
   * 근로자의 날(5/1) 수당 — 토글 ON일 때 totalSalary 에 합산된 실금액.
   * OFF 또는 5월이 아닌 경우 0.
   * 설계문서: required/근로자의날.md §4
   */
  laborDayAllowance?: number;
  /**
   * 근로자의 날 수당 분해 (토글과 무관하게 항상 계산).
   * 5월이 아닌 경우 null. UI 말풍선·바텀시트·PDF 표시용.
   */
  laborDayBreakdown?: LaborDayAllowanceBreakdown | null;
  /** 사장님이 설정한 노동절 합산 토글 상태 */
  laborDayIncluded?: boolean;
  /** 일당·용역 근무일(건)수 */
  workDays?: number;
}

export interface WeeklyHolidayDetail {
  weekNumber: number;
  weekStart: Date;
  weekEnd: Date;
  weekStartFormatted: string;
  weekEndFormatted: string;
  totalWorkHours: number;
  isEligible: boolean;          // 주휴수당 지급 자격 여부
  holidayAllowance: number;
  workDays: Array<{
    date: Date;
    startTime: string;
    endTime: string;
    workHours: number;
    breakTime: string;
    timeDisplay?: string; // 템플릿 기반 스케줄의 여러 시간 구간 표시용
  }>;
  workDaysCount: number;
}

// 탭 관련 타입
export type EmployeeManagementTab = 'calendar' | 'payroll' | 'employees' | 'records';

// 컴포넌트 Props 타입
export interface EmployeeManagementProps {
  // userId는 useAccountBookStore에서 가져옴
  /** 진입 시 활성화할 탭 (문서관리 → 인사기록 연동용) */
  initialTab?: EmployeeManagementTab;
}

export interface TabNavigationProps {
  activeTab: EmployeeManagementTab;
  onTabChange: (tab: EmployeeManagementTab) => void;
  employees?: Employee[];
  selectedMonth?: Date;
  accountNumber?: number;
  /** 오버라이드 반영된 5인 이상 여부. 전달 시 탭 라벨에 사용 */
  isBusinessScale5Plus?: boolean;
}

export interface CalendarTabProps {
  // userId는 useAccountBookStore에서 가져옴
}

export interface PayrollTabProps {
  selectedMonth?: Date;
  onMonthChange?: (date: Date) => void;
  /** 오버라이드 반영된 5인 이상 여부. 급여 가산 계산에 사용 */
  isBusinessScale5Plus?: boolean;
  /** 노동절(5/1) 가산 5인 이상 판정 — 전월 말일 기준. 설계문서 §7 */
  laborDayIs5Plus?: boolean;
  /** 노동절 합산 토글 hook 결과 (직원별 ON/OFF 관리) */
  laborDayToggle?: import('../hooks/useLaborDayToggle').UseLaborDayToggleResult;
  /** Firestore 동기화가 비활성화된 사용자(비로그인/무료) 안내용 */
  laborDaySyncDisabled?: boolean;
}

export interface EmployeeTabProps {
  // userId는 useAccountBookStore에서 가져옴
}
